import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ContentType, Platform, ResultFilters, SortOption } from '../types/api';

const PLATFORMS: readonly string[] = ['instagram', 'facebook'];
const SORTS: readonly string[] = ['relevance', 'recent', 'engagement'];
const CONTENT_TYPES: readonly string[] = [
  'post',
  'reel',
  'video',
  'photo',
  'story',
  'comment',
  'page_post',
  'unknown',
];

export const DEFAULT_FILTERS: ResultFilters = { sort: 'relevance' };

/**
 * Filtros vivem na URL (RF-F12).
 *
 * Isso resolve três coisas de uma vez: recarregar mantém a visão, o botão
 * voltar funciona como o usuário espera, e a tela filtrada pode ser
 * compartilhada com outra pessoa da equipe.
 */
export function useResultFilters(): {
  filters: ResultFilters;
  setFilter: <K extends keyof ResultFilters>(key: K, value: ResultFilters[K] | undefined) => void;
  clear: () => void;
  hasActiveFilters: boolean;
} {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<ResultFilters>(() => {
    const platform = params.get('platform');
    const type = params.get('type');
    const sort = params.get('sort');
    const author = params.get('author')?.trim();
    const keyword = params.get('keyword')?.trim();

    return {
      // Valor inválido na URL é ignorado, não quebra a tela: a query string é
      // entrada do usuário como qualquer outra.
      platform: platform && PLATFORMS.includes(platform) ? (platform as Platform) : undefined,
      type: type && CONTENT_TYPES.includes(type) ? (type as ContentType) : undefined,
      sort: sort && SORTS.includes(sort) ? (sort as SortOption) : 'relevance',
      author: author ? author : undefined,
      keyword: keyword ? keyword : undefined,
    };
  }, [params]);

  const setFilter = useCallback(
    <K extends keyof ResultFilters>(key: K, value: ResultFilters[K] | undefined): void => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === undefined || value === '' || (key === 'sort' && value === 'relevance')) {
            next.delete(key);
          } else {
            next.set(key, String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const clear = useCallback((): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const key of ['platform', 'type', 'sort', 'author', 'keyword']) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  const hasActiveFilters =
    Boolean(filters.platform ?? filters.type ?? filters.author ?? filters.keyword) ||
    filters.sort !== 'relevance';

  return { filters, setFilter, clear, hasActiveFilters };
}
